// packages/chordpro/src/section-map.js
//
// Единственная правда о том, какой блок документа соответствует какой секции
// аранжировки. Раздача ЧИСТАЯ: ни DOM, ни геометрии, ни времени — только списки.
// Её берут обе поверхности (десктопный лист и веб-ведение), потому что
// расхождение здесь означало бы, что лидер видит один блок, а зал другой.
//
// Связь ХРАНИТСЯ (`song.chart.links = { sectionId: 'заголовок как написан' }`).
// Прежняя модель выводила её из имён, и для русского чарта не находила ни одного
// блока: `Куплет 1` и `Verse 1` — разные строки. Вывод по имени остаётся
// РОВНО одним случаем — связей нет вовсе (шейр, опубликованный до волны D).

import { linkTargets } from './link.js';

/**
 * Секции в порядке ВРЕМЕНИ. Порядок массива не гарантирован ничем: секцию можно
 * добавить в конец списка, а поставить в начало песни. Раздача копий припева
 * последовательна, поэтому неверный порядок молча подсветил бы не тот блок.
 *
 * Сортировка СТАБИЛЬНА и терпима: равные и нечисловые начала держат порядок
 * массива, а не выпадают — секция без начала это данные, а не повод потерять её.
 * null/undefined-элемент — та же терпимость, что у sectionIndexAtBar и
 * mapSectionsToBlocks ниже: getStart() не зовётся на нём вовсе (звать значило
 * бы падать вместо терпеть), элемент держит место по стабильности сортировки.
 *
 * @template T
 * @param {T[]} sections
 * @param {(s:T)=>number} getStart такт или секунда начала — любая монотонная ось
 * @returns {T[]}
 */
export function orderByTime(sections, getStart) {
  const src = Array.isArray(sections) ? sections : [];
  return src
    .map((s, i) => ({ s, i, t: s ? Number(getStart(s)) : NaN }))
    .sort((a, b) => {
      const af = Number.isFinite(a.t);
      const bf = Number.isFinite(b.t);
      if (af && bf && a.t !== b.t) return a.t - b.t;
      if (af !== bf) return af ? -1 : 1;
      return a.i - b.i;
    })
    .map((x) => x.s);
}

/**
 * Секция, внутри которой стоит позиция, или −1 — «вне расписания». Начало секции
 * ей принадлежит, конец — уже нет: иначе на стыке две секции были бы текущими
 * одновременно. Ось — такты (десктоп); у веба своя копия правила в секундах
 * (`sectionIndexAt` в chart-follow.ts), и сводить их в одну функцию нельзя, пока
 * поверхности живут в разных пакетах.
 *
 * @param {ReadonlyArray<{startBar:number, endBar:number}>} sections в порядке времени
 * @param {number} bar дробная позиция в тактах
 * @returns {number}
 */
export function sectionIndexAtBar(sections, bar) {
  const src = Array.isArray(sections) ? sections : [];
  for (let i = 0; i < src.length; i++) {
    const s = src[i];
    if (!s) continue;
    if (bar >= s.startBar && bar < s.endBar) return i;
  }
  return -1;
}

/**
 * Секция → индекс блока документа (или null).
 *
 * @param {ReadonlyArray<{id:string, name?:string}>} sections УЖЕ в порядке времени
 * @param {ReadonlyArray<{name:string|null, ref?:boolean}>} blocks в порядке документа
 * @param {Record<string,string>|null|undefined} links хранимые связи; отсутствие = наследие
 * @returns {Array<number|null>} по элементу на секцию
 */
export function mapSectionsToBlocks(sections, blocks, links) {
  const secs = Array.isArray(sections) ? sections : [];
  const blks = Array.isArray(blocks) ? blocks : [];
  const stored = links && typeof links === 'object' && !Array.isArray(links) ? links : null;

  // Отсылка `{chorus}` ВЕДЁТСЯ наравне с заголовком (решение Сергея 03.09).
  //
  // Она уцелевает ровно тогда, когда припева в документе нет вовсе — иначе
  // `renderList` развернул бы её копией, — то есть плашка это единственное, чем
  // документ представляет припев. Не подсветить её значит не подсветить ничего
  // на весь припев, и читателю на стойке нечем отличить это от сломанного
  // ведения. Прежнее правило («печатать в неё нечего») держало ДВЕ поверхности
  // разными: веб плашку вёл, десктоп нет.
  //
  // Амбра при этом не врёт: она говорит «песня ЗДЕСЬ, в документе», а не «слова
  // здесь» — слов на плашке не напечатано, и это видно.
  const heads = blks.map((b) => (b && typeof b.name === 'string' ? b.name.trim() : null));
  const names = secs.map((s) => (s && typeof s.name === 'string' ? s.name : ''));

  // Наследие считается ОДИН раз и в ту же сторону, что до волны D: у блока
  // спрашивают, какие секции ему подходят. Обратный порядок дал бы другой ответ —
  // ступени `linkTargets` глобальны по списку имён.
  const legacyTargets = stored ? null : heads.map((h) => (h ? new Set(linkTargets(h, names)) : new Set()));

  const candidatesOf = (sec, secName) => {
    const out = [];
    if (stored) {
      const want = stored[sec?.id];
      if (typeof want !== 'string' || !want.trim()) return out;
      const w = want.trim();
      for (let i = 0; i < heads.length; i++) if (heads[i] === w) out.push(i);
      return out;
    }
    if (!secName.trim()) return out;                 // безымянной нечем связаться по имени
    for (let i = 0; i < legacyTargets.length; i++) if (legacyTargets[i].has(secName)) out.push(i);
    return out;
  };

  const taken = new Array(blks.length).fill(false);
  return secs.map((sec, si) => {
    const cands = candidatesOf(sec, names[si]);
    let last = -1;
    for (const bi of cands) {
      last = bi;
      if (!taken[bi]) { taken[bi] = true; return bi; }
    }
    // Незанятых копий больше нет: секций припева в аранжировке больше, чем копий
    // в документе. Потерять подсветку хуже, чем показать копию повторно.
    return last >= 0 ? last : null;
  });
}

/**
 * Лид ведения — на сколько тактов лист опережает песню. Читать вперёд надо
 * ДО того, как секция началась; подсвечивать вперёд — нельзя (это ложное
 * утверждение «мы здесь»). Отсюда два индекса и одно правило ниже.
 *
 * Константа, а не настройка — решение Сергея 03.09: крутилка добавляется
 * поверх, если понадобится, и ничего здесь не переписывает.
 */
export const LEAD_BARS = 2;

/**
 * Что красить при ведении с лидом. Единиц не знает: обе поверхности считают
 * свои индексы сами (десктоп в тактах, веб в секундах) и зовут это.
 *
 * Правило одно, и оно здесь, потому что разойдётся именно оно: «метки нет,
 * когда лид указывает в тот же блок / блока нет / лид за концом». Написанное
 * дважды, оно и есть механизм B-260829 — один список под две задачи.
 *
 * @param {number} nowIdx индекс секции, которая звучит, или −1 (вне расписания)
 * @param {number} leadIdx индекс секции через LEAD_BARS, или −1
 * @param {(si:number) => number} blockOf индекс блока листа по индексу секции, −1 если блока нет;
 *   терпит и не-число (`null` — конвенция mapSectionsToBlocks, `undefined` — за
 *   пределами массива) — это тоже читается как «блока нет», а не как 0
 * @returns {{nowBlock:number, nextBlock:number, scrollBlock:number}} −1 = «нет»
 */
export function followTargets(nowIdx, leadIdx, blockOf) {
  const at = (si) => {
    // `!(si >= 0)`, а не `si < 0`: вторая форма пропускает NaN (`NaN < 0` —
    // false), и NaN-индекс молча дошёл бы до blockOf вместо того, чтобы
    // сразу читаться как «нет секции».
    if (!(si >= 0)) return -1;
    const bi = blockOf(si);
    // typeof-проверка обязательна: mapSectionsToBlocks в этом же файле отдаёт
    // null там, где блока нет, а `null >= 0` в JS истинно — без проверки типа
    // null проскочил бы как индекс 0.
    return typeof bi === 'number' && bi >= 0 ? bi : -1;
  };
  const nowBlock = at(nowIdx);
  const lead = at(leadIdx);
  // Метка «дальше» — только когда она СООБЩАЕТ ЧТО-ТО НОВОЕ. Тот же блок под
  // курсором «сейчас» уже покрашен амброй, и вторая метка на нём спорила бы с
  // ней вместо того, чтобы дополнять.
  const nextBlock = lead >= 0 && lead !== nowBlock ? lead : -1;
  // Пока метка есть — ведём к ней, это и есть «лист уезжает раньше». Как только
  // лид перестал отличаться, цель прокрутки — текущий блок.
  const scrollBlock = nextBlock >= 0 ? nextBlock : nowBlock;
  return { nowBlock, nextBlock, scrollBlock };
}

/**
 * Лид в СЕКУНДАХ — для поверхностей, которые ведут по времени, а не по тактам
 * (веб: `sectionIndexAt(sections, posSec)`).
 *
 * Парсит размер такта сам, и это осознанная вторая копия правила: веб живёт в
 * другом пакете и `app/lib/bar-grid.mjs` ему недоступен. Копия заперта тестом
 * `app/tests/renderer/lead-bars-parser-parity.test.js` — он гоняет одну таблицу
 * размеров через оба парсера и требует одинаковый ответ.
 *
 * Регулярка строгая — ровно `parseBeatsPerBar` из `bar-grid.mjs` (он объявлен
 * правым, им живёт весь десктопный бар-грид), а не «допускать побольше форм».
 * Терпимая версия (пробелы вокруг, слэш опционален) расходилась с bar-grid
 * МОЛЧА на ' 3/4', '3/4 ', '3/', '3/4/8', '3/-4' — замок-паритет проверял
 * формат, а не грамматику, и не видел этих строк (ревью качества, 03.09).
 * Знаменатель разбирается, но в расчёт не идёт — так же, как у bar-grid.
 *
 * @param {number} bpm темп; 0/отрицательный/NaN = «темп не задан»
 * @param {string|null|undefined} timeSignature «4/4»; мусор и отсутствие = 4 доли
 * @returns {number} секунды; 0 когда темпа нет — тогда лида не существует
 */
export function leadSeconds(bpm, timeSignature) {
  const t = Number(bpm);
  // Темпа нет — лида нет. Ноль здесь безопаснее любого другого ответа: позиция
  // `posSec + 0` это ровно сегодняшнее поведение, а `posSec + Infinity`
  // выбросило бы ведение за расписание насовсем.
  if (!Number.isFinite(t) || t <= 0) return 0;
  const m = typeof timeSignature === 'string' ? /^(\d+)\/(\d+)$/.exec(timeSignature) : null;
  const beats = m ? Number(m[1]) : 0;
  const beatsPerBar = Number.isFinite(beats) && beats > 0 ? beats : 4;
  return LEAD_BARS * beatsPerBar * 60 / t;
}
